import React, { useState, useEffect, useContext } from 'react';
import orderBy from 'lodash/orderBy';
import moment from 'moment';
import DateFnsUtils from '@date-io/moment';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import FormControl from '@material-ui/core/FormControl';
import Tooltip from '@material-ui/core/Tooltip';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';

import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';

// icon
import GetAppIcon from '@material-ui/icons/GetApp';
import { StyledFormButton } from '../../../../components/button/Button';

// Provider
import ModeContext from '../../../../context/ModeContext';
import UserContext from '../../../../context/UserContext';

// component
import DamModal from '../../../../components/modal/Modal';
import DamLegend from '../../legend/DamLegend';
import DamLargeLegend from '../../legend/DamLargeLegend';
import DamLargeDataListsEgat from './DamLargeDataListsEgat';
import areas from '../../../../assets/json/region';

import { globalStyle } from '../../../../global/style';

// export config
const headers = [
  { label: 'อ่างเก็บน้ำ', key: 'dam_name' },
  { label: 'จังหวัด', key: 'province_name' },
  { label: 'ลุ่มน้ำ', key: 'basin_name' },
  { label: 'ระดับน้ำ (ม.รทก.)', key: 'dam_level' },
  { label: 'ความจุ (ล้าน ลบ.ม.)', key: 'max_storage' },
  { label: 'ปริมาณน้ำกักเก็บ (ล้าน ลบ.ม.)', key: 'dam_storage' },
  { label: 'คิดเป็น % ของน้ำเต็มอ่าง', key: 'percent_storage' },
  { label: 'ปริมาณน้ำไหลลงอ่าง (ล้าน ลบ.ม.)', key: 'dam_inflow' },
  { label: 'ปริมาณน้ำระบาย (ล้าน ลบ.ม.)', key: 'dam_released' },
];
const datas = [];

// set total summary
let grandTotalInflow = 0;
let grandTotalReleased = 0;
let grandTotalDamStorage = 0;
let grandTotalMaxStorage = 0;

export default function DamLargeListsEgatContainer({ useRuleCurve }) {
  const { t } = useTranslation();
  const [datasLargeDamEgat, setLargeDamDataEgat] = useState([]);
  const [selectedDateEgat, setSelectedDateEgat] = useState('');
  const [maxDate, setMaxDate] = useState('');
  const [error, setError] = useState('');

  const [csv, setCsv] = useState({
    headers: [],
    data: [],
  });

  const useStyles = makeStyles(globalStyle);
  const classes = useStyles();

  // modal
  const [modalData, setModalData] = useState({
    open: false,
    header: t('กราฟอ่างเก็บน้ำรายวัน การไฟฟ้า'),
    subheader: null,
    content: null,
  });

  // get context
  const { currentMode, currentArea } = useContext(ModeContext);
  const { userIsLogin } = useContext(UserContext);

  function filterData(currentData, mode, area) {
    let filterD = [];

    if (mode === 'province' && area.code !== '0') {
      filterD = currentData.filter((d) => d.geocode.province_code === area.code);
    } else if (currentMode === 'basin' && area.code !== '0') {
      filterD = currentData.filter((d) => d.basin.basin_code === parseInt(area.code, 10));
    } else {
      filterD = currentData;
    }

    return filterD;
  }

  const fetchData = async (d) => {
    let url = `${process.env.REACT_APP_API_URL}/analyst/dam?dam_size=1&dam_date=${d}`;
    if (d === undefined) {
      url = `${process.env.REACT_APP_API_URL}/analyst/dam?dam_size=1`;
    }

    fetch(url)
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('Something went wrong ...');
      })
      .then((result) => {
        /*
        Add filter by province or basin later
        */
        const datasDamDailyEgat = orderBy(result.data.dam_daily, ['dam_storage_percent'], ['desc']);
        // custom render export data
        if (areas.length === 7) {
          // remove อื่นๆ
          areas.splice(-1, 1);
        }

        areas.map((area) => {
          datas.push({
            dam_name: area.name,
          });

          // summary value by region
          let totalInflow = 0;
          let totalReleased = 0;
          let totalDamStorage = 0;
          let totalMaxStorage = 0;

          datasDamDailyEgat
            .filter((row) => row.geocode.area_code === area.id.toString() && row.agency.id === 8)
            .map((row) => {
              totalInflow += row.dam_inflow;
              totalReleased += row.dam_released;
              if (!Number.isNaN(parseFloat(row.dam.max_storage))) {
                totalMaxStorage += row.dam.max_storage;
              }
              totalDamStorage += row.dam_storage;

              grandTotalInflow += row.dam_inflow;
              grandTotalReleased += row.dam_released;
              grandTotalDamStorage += row.dam_storage;
              if (!Number.isNaN(parseFloat(row.dam.max_storage))) {
                grandTotalMaxStorage += row.dam.max_storage;
              }
              grandTotalMaxStorage += row.dam.max_storage;

              // calculate percent storage
              let percentStorage = 0;
              percentStorage = ((100 * row.dam_storage) / row.dam.max_storage)
                .toFixed(0);

              datas.push({
                dam_name: row.dam.dam_name.th,
                province_name: row.geocode.province_name.th,
                basin_name: row.basin.basin_name.th,
                dam_level: row.dam_level,
                max_storage: row.dam.max_storage,
                dam_storage: row.dam_storage,
                percent_storage: !Number.isNaN(parseFloat(percentStorage), 10) ? percentStorage : '-',
                dam_inflow: row.dam_inflow,
                dam_released: row.dam_released,
              });

              return null;
            });

          // push summary data for each region
          // const regionDamStoragePercent = !Number.isNaN(totalMaxStorage, 10) ? ((totalDamStorage * 100) / totalMaxStorage).toFixed(0) : '-';

          datas.push({
            dam_name: t(`รวม${area.name}`),
            max_storage: totalMaxStorage,
            dam_storage: totalDamStorage,
            percent_storage: '-',
            dam_inflow: totalInflow,
            dam_released: totalReleased,
          });

          return null;
        });

        // grand total
        const allTotalMaxStorage = !Number.isNaN(grandTotalMaxStorage, 10) ? grandTotalMaxStorage.toFixed(0) : '-';
        const allDamStoragePercent = !Number.isNaN(grandTotalMaxStorage, 10) ? ((grandTotalDamStorage * 100) / grandTotalMaxStorage).toFixed(0) : '-';

        datas.push({
          dam_name: t('รวมทั้งประเทศ'),
          max_storage: allTotalMaxStorage,
          dam_storage: grandTotalDamStorage,
          dam_storage_percent: allDamStoragePercent,
          dam_inflow: grandTotalInflow,
          dam_released: grandTotalReleased,
        });

        // set data state
        setLargeDamDataEgat(datasDamDailyEgat);
        setSelectedDateEgat(result.data.dam_daily[0].dam_date);
        if (!d) setMaxDate(result.data.dam_daily[0].dam_date);
        setCsv({
          headers,
          data: datas,
        });
      })
      .catch((err) => setError(err));
  };

  const handleModalChanage = (d) => {
    setModalData(d);
  };

  const handleDateChange = (d) => {
    const myDate = moment(d).format('YYYY-MM-DD');
    setSelectedDateEgat(myDate);

    fetchData(myDate);

    handleModalChanage({
      open: false,
      header: t('กราฟอ่างเก็บน้ำรายวัน กรมชลประทาน'),
      subheader: null,
      content: null,
    });
  };

  // fetch data
  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {error && <div>Error Occured</div>}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={9}>
          <div style={{ paddingTop: '35px' }}>
            <Typography variant="h5" component="h5">
              {`${t('ข้อมูลวันที่')} ${selectedDateEgat ? moment(selectedDateEgat).locale('th').format('DD MMM YYYY') : ''}`}
            </Typography>
          </div>
        </Grid>
        <Grid item xs={8} sm={4} md={2}>
          {/* Search and Download */}
          <FormControl>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                margin="normal"
                variant="inline"
                format="YYYY-MM-DD"
                id="date-picker"
                label={t('ข้อมูลวันที่')}
                value={selectedDateEgat || moment().subtract(1, 'days').format('YYYY-MM-DD')}
                maxDate={maxDate}
                onChange={handleDateChange}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
              />
            </MuiPickersUtilsProvider>
          </FormControl>
        </Grid>
        <Grid item xs={4} sm={2} md={1}>
          {userIsLogin && (
            <div style={{ paddingTop: '35px' }}>
              <CSVLink id="download_csv" data={csv.data} headers={csv.headers} filename={`dam_large_egat_${selectedDateEgat}.csv`}>
                <Tooltip title={t('ดาวน์โหลดไฟล์ อ่างเก็บน้ำ')} placement="top">
                  <StyledFormButton
                    variant="outlined"
                    size="small"
                    className={classes.button}
                    startIcon={<GetAppIcon />}
                  >
                    {t('ดาวน์โหลด')}
                  </StyledFormButton>
                </Tooltip>
              </CSVLink>
            </div>
          )}
        </Grid>
        <Grid item xs={12} sm={12} md={12}>
          <Box>
            <DamLargeDataListsEgat
              datas={filterData(datasLargeDamEgat, currentMode, currentArea)}
              latestDate={selectedDateEgat}
              handleModalChanage={handleModalChanage}
              useRuleCurve={useRuleCurve}
            />
          </Box>
          <Box mt={2}>
            {useRuleCurve ? <DamLargeLegend /> : <DamLegend />}
          </Box>
          <Box mt={2}>
            <Typography variant="subtitle1" display="block" fontSize="15px">{t('คำย่อ')}</Typography>
            <Typography variant="body2" display="block" fontSize="12px">{t('รนส. หมายถึง ระดับน้ำสูงสุด')}</Typography>
            <Typography variant="body2" display="block" fontSize="12px">{t('รนก. หมายถึง ระดับน้ำกับเก็บ')}</Typography>
          </Box>
          <Box mt={2} align="right">
            {t('ข้อมูลจาก EGAT')}
          </Box>
        </Grid>
      </Grid>

      <DamModal
        open={modalData.open}
        modalHeader={modalData.header}
        modalSubHeader={modalData.subheader}
        modalContent={modalData.content}
      />
    </>
  );
}

DamLargeListsEgatContainer.propTypes = {
  useRuleCurve: PropTypes.instanceOf(Boolean).isRequired,
};
